import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  Autocomplete,
  Collapse,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { useApiQuery, useApiMutation, useCreateJob } from "../../hooks/useApi"

//Dummy Data
const mockClients = [
  { _id: "1", name: "John Doe", email: "john@example.com" },
  { _id: "2", name: "Jane Smith", email: "jane@example.com" },
  { _id: "3", name: "Raj Singh", email: "raj@local.in" },
  { _id: "4", name: "Shweta Kumar", email: "shweta@domain.com" },
];

const mockJobTypes = [
  { _id: "1", name: "ITR Filing" },
  { _id: "2", name: "GST Return" },
];

const mockUsers = [
  { _id: "u1", name: "Admin One" },
  { _id: "u2", name: "Support Two" },
  { _id: "u3", name: "Finance Three" },
];

function AddJobPage() {

  const { data: clients } = useApiQuery(["clients"], "/clients/");
  const { data: users } = useApiQuery(["users"], "/users/");
  const { data: jobList } = useApiQuery(["jobs-list"], "/job-list/");

  const displayClients = clients?.clients || mockClients;
  const displayJobs = jobList || mockJobTypes;
  const displayUsers = users?.filter(u => u.role == 'normal') || [];

  const createJobMutation = useCreateJob()

  const [form, setForm] = useState({
    clientId: null,
    jobTypeId: null,
    assignedTo: null,
    secondaryUser: [],
    fees: "",
    description: "",
    dueDate: "",
  });
  const [alert, setAlert] = useState({ show: false, type: "", text: "" });
  const [clientInput, setClientInput] = useState("");

  // Filter out the assigned user from secondary user options
  const availableSecondaryUsers = useMemo(() => {
    if (!form.assignedTo) return [];
    return displayUsers.filter(user => user._id !== form.assignedTo);
  }, [displayUsers, form.assignedTo]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelect = (field, value) => {
    setForm({ ...form, [field]: value ? value._id : null });
    
    // Clear secondary user if assigned to changes
    if (field === "assignedTo") {
      setForm(prev => ({ ...prev, [field]: value ? value._id : null, secondaryUser: [] }));
    }
  };

  const handleSecondaryUserChange = (_, value) => {
    // Extract just the _id values from the selected users
    const secondaryUserIds = value.map(user => user._id);
    setForm({ ...form, secondaryUser: secondaryUserIds });
  };

  const handleSubmit = (e) => {
    e.preventDefault()
    createJobMutation.mutate(
      form, // this will be passed as "data" in your mutationFn
      {
        onSuccess: () => {
          setAlert({ show: true, type: "success", text: "Job created successfully!" });
          setTimeout(() => setAlert({ show: false, type: "", text: "" }), 1500);
          setForm({
            clientId: null,
            jobTypeId: null,
            assignedTo: null,
            secondaryUser: [],
            fees: "",
            description: "",
            dueDate: "",
          });
          setClientInput("");
        },
        onError: (error) => {
          setAlert({ show: true, type: "error", text: "Form submission failed" });
          console.error("Error creating job:", error)
        },
      }
    )
  }

  const handleCancel = () => {
    setForm({
      clientId: null,
      jobTypeId: null,
      assignedTo: null,
      secondaryUser: [],
      fees: "",
      description: "",
      dueDate: "",
    });
    setClientInput("");
    setAlert({ show: true, type: "info", text: "Form cleared" });
    setTimeout(() => setAlert({ show: false, type: "", text: "" }), 1500);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Add New Job
      </Typography>
      {alert.show && <Alert severity={alert.type} sx={{ mb: 3 }}>{alert.text}</Alert>}
      <Paper sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit} autoComplete="off">
          <Grid container spacing={3}>
            {/* Client */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={
                  clientInput.length < 1
                    ? []
                    : displayClients.filter((opt) =>
                        opt.name.toLowerCase().includes(clientInput.toLowerCase()) ||
                        (opt.phoneNumber && opt.phoneNumber.includes(clientInput))
                      )
                }
                getOptionLabel={(opt) =>
                  opt?.name ? `${opt.name} (${opt?.phoneNumber || ""})` : ""
                }
                value={displayClients?.find((c) => c._id === form.clientId) || null}
                onInputChange={(_, value) => setClientInput(value)}
                onChange={(_, value) => handleSelect("clientId", value)}
                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client"
                    required
                  />
                )}
              />
            </Grid>

            {/* Job Type */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={displayJobs}
                getOptionLabel={(opt) => opt?.name || ""}
                value={displayJobs.find((t) => t._id === form.jobTypeId) || null}
                onChange={(_, value) => handleSelect("jobTypeId", value)}
                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Job Type"
                    required
                  />
                )}
              />
            </Grid>

            {/* Assigned To */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={displayUsers}
                getOptionLabel={(opt) => opt?.name || ""}
                value={displayUsers.find((u) => u._id === form.assignedTo) || null}
                onChange={(_, value) => handleSelect("assignedTo", value)}
                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                renderInput={(params) => (
                  <TextField
                    required
                    {...params}
                    label="Assigned To"
                  />
                )}
              />
            </Grid>

            {/* Due Date */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Due Date"
                name="dueDate"
                type="date"
                value={form.dueDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                inputProps={{min: new Date().toISOString().split('T')[0]}}
              />
            </Grid>

            {/* Secondary User - Only visible if assignedTo is set */}
            <Grid item xs={12}>
              <Collapse in={!!form.assignedTo}>
                <Autocomplete
                  multiple
                  options={availableSecondaryUsers}
                  getOptionLabel={(opt) => opt?.name || ""}
                  value={availableSecondaryUsers.filter(user => 
                    form.secondaryUser.includes(user._id)
                  )}
                  onChange={handleSecondaryUserChange}
                  isOptionEqualToValue={(opt, val) => opt._id === val._id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Secondary Users (Optional)"
                      placeholder={form.assignedTo ? "Add secondary users..." : ""}
                      helperText="Select one or more users to assist with this job"
                    />
                  )}
                  disabled={!form.assignedTo}
                />
              </Collapse>
            </Grid>
            {/* Description */}
            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                value={form.description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
                placeholder="Add job details, requirements, or notes..."
              />
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={createJobMutation.isLoading}
            >
              {createJobMutation.isLoading ? "Creating..." : "Create Job"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={createJobMutation.isLoading}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default AddJobPage;